import { Divider, Grid, Typography, useTheme } from "@mui/material";
import {
  useGetCountriesQuery,
  useGetPartnerEligibleCountiesQuery,
} from "api/api-misc";
import Modal from "components/Common/Modal";
import SkeletonLoad from "components/Common/SkeletonLoad";
import useIsMobile from "hooks/useIsMobile";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import secondaryButtonStyle from "styles/secondaryButtonStyle";

import Drop from "components/Common/Drop";
import styles from "./countryeligibilitymodal.module.scss";

const CountryEligibilityModal = ({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [supplierCountry, setSupplierCountry] = useState("");
  const [buyerCountry, setBuyerCountry] = useState("");

  const [isChecking, setIsChecking] = useState(true);
  const isEligible = {
    color: "#9fce63",
    border: "0.25emsolid #9fce63",
  };
  const isIneligible = {
    color: "#ff0000",
    border: "0.25emsolid #ff0000",
  };
  const {
    data: sellerEligibleCountries,
    isFetching: isFetchingSellerEligibleCountries,
  } = useGetPartnerEligibleCountiesQuery("seller");
  const {
    data: buyerEligibleCountries,
    isFetching: isFetchingBuyerEligibleCountries,
  } = useGetPartnerEligibleCountiesQuery("buyer");
  const { data: countries, isFetching: isFetchingCountries } =
    useGetCountriesQuery();

  const retry = () => {
    setIsChecking(true);
    setSupplierCountry("");
    setBuyerCountry("");
  };

  const supplierSetter = (value: string) => {
    setSupplierCountry(value);
  };
  const buyerSetter = (value: string) => {
    setBuyerCountry(value);
  };
  const modalOverlaySx = {
    backgroundColor: "white !important",
    width: "50%",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4ch",
    padding: "2ch",
    fontSize: `${theme.typography.fontSize}px !important`,
    fontFamily: `${theme.typography.fontFamily} !important`,
    height: "inherit !important",
    fontWeight: "inherit !important",
  };
  const modalSx = {
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  };
  return (
    <Modal
      Header={
        <Grid item margin="auto">
          <Typography fontWeight="bold" fontSize="1.3em" textAlign="center">
            Check Your Country Eligibility
          </Typography>
        </Grid>
      }
      message={
        <Grid container spacing={2} margin="auto">
          <Grid item xs={12}>
            <Divider />
          </Grid>
          {isFetchingCountries && <SkeletonLoad bars={2} />}
          {countries && !isFetchingCountries && (
            <>
              <Grid item xs={12} lg={6}>
                <Drop
                  label="Select Supplier Country"
                  name=""
                  keyValue="name"
                  data={countries}
                  setValue={supplierSetter}
                  value={supplierCountry}
                  disabled={
                    !isChecking &&
                    Boolean(supplierCountry) &&
                    Boolean(buyerCountry)
                  }
                  fullWidth
                />
                {!isChecking &&
                  Boolean(supplierCountry) &&
                  Boolean(buyerCountry) && (
                    <div
                      className={`${styles.stamp} `}
                      style={
                        Boolean(
                          sellerEligibleCountries?.find(
                            (country) => country.name === supplierCountry
                          )
                        )
                          ? isEligible
                          : isIneligible
                      }
                    >
                      {Boolean(
                        sellerEligibleCountries?.find(
                          (country) => country.name === supplierCountry
                        )
                      )
                        ? "eligible"
                        : "ineligible"}
                    </div>
                  )}
              </Grid>
              <Grid item xs={12} lg={6}>
                <Drop
                  label="Select Buyer Country"
                  name=""
                  keyValue="name"
                  data={countries}
                  setValue={buyerSetter}
                  value={buyerCountry}
                  disabled={
                    !isChecking &&
                    Boolean(supplierCountry) &&
                    Boolean(buyerCountry)
                  }
                  fullWidth
                />
                {!isChecking &&
                  Boolean(supplierCountry) &&
                  Boolean(buyerCountry) && (
                    <div
                      className={`${styles.stamp} `}
                      style={
                        Boolean(
                          buyerEligibleCountries?.find(
                            (country) => country.name === buyerCountry
                          )
                        )
                          ? isEligible
                          : isIneligible
                      }
                    >
                      {Boolean(
                        buyerEligibleCountries?.find(
                          (country) => country.name === buyerCountry
                        )
                      )
                        ? "eligible"
                        : "ineligible"}
                    </div>
                  )}
              </Grid>
            </>
          )}
          {isChecking &&
            isFetchingSellerEligibleCountries &&
            isFetchingBuyerEligibleCountries && <SkeletonLoad bars={2} />}
          {!isChecking && Boolean(supplierCountry) && Boolean(buyerCountry) && (
            <Grid item xs={12}>
              {Boolean(
                sellerEligibleCountries?.find(
                  (country) => country.name === supplierCountry
                )
              ) &&
              Boolean(
                buyerEligibleCountries?.find(
                  (country) => country.name === buyerCountry
                )
              )
                ? "Eligible to provide invoice financing for this trade"
                : "Unfortunately, Incomlend cannot provide invoice financing for this trade"}
            </Grid>
          )}
        </Grid>
      }
      sx={modalSx}
      width={isMobile ? "95%" : "40%"}
      height={isMobile ? "auto" : "auto"}
      modalContentSx={{ height: "auto" }}
      open={show}
      modalOverlaySx={modalOverlaySx}
      modalFooterSx={{ maxWidth: "100%" }}
      onClose={onClose}
      primary={{
        variant: "contained",

        color:
          !isChecking && Boolean(supplierCountry) && Boolean(buyerCountry)
            ? "error"
            : "primary",
        onClick:
          !isChecking && Boolean(supplierCountry) && Boolean(buyerCountry)
            ? retry
            : () => setIsChecking(false),
        children:
          !isChecking && Boolean(supplierCountry) && Boolean(buyerCountry)
            ? "Retry"
            : "Check now",
      }}
      closeButton
      secondary={{
        sx: secondaryButtonStyle,
        variant: "outlined",
        onClick: onClose,
        children: `${t("cancel")}`,
      }}
    />
  );
};
export default CountryEligibilityModal;
